import React, { useState } from 'react';
import { Form, Input, Card, Checkbox, Button, message } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import { useHistory, useLocation } from 'react-router-dom';
import { getWeboConfig } from '../../utils';
import { AuthWrapper, FormWrapper } from './Auth.styled';
import { FlexDivCenter, Text } from '../../components/Typography/Typography';
import UseMedia from '../../customHooks/UseMedia';
import {
  loginAction,
  fetchActiveUserAction,
  handleTwoFactorAction,
} from '../../data/modules/auth/auth.actions';

export default () => {
  const { t } = useTranslation();
  const { isMobile } = UseMedia();
  const [form] = Form.useForm();
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [authStep, setAuthStep] = useState('login');
  const [detailsForTwoFactor, setDetailsForTwoFactor] = useState();

  function onLoginFinish(e) {
    e.preventDefault && e.preventDefault();
    form.validateFields().then((credentials) => {
      setIsLoading(true);
      dispatch(loginAction(credentials))
        .then((_) => {
          setAuthStep('2fa');
          setDetailsForTwoFactor({
            idNumber: credentials.idNumber,
            email: credentials.email,
          });
        })
        .catch((err) => {
          if (err.response.status >= 400) {
            message.error({
              content: t('bad_credentials'),
              style: {
                marginTop: '10vh',
                fontSize: 16,
              },
            });
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    });
  }

  function onTwoFactorFinish(e) {
    e.preventDefault && e.preventDefault();
    form.validateFields().then(({ verificationCode }) => {
      setIsLoading(true);
      dispatch(
        handleTwoFactorAction({ verificationCode, ...detailsForTwoFactor })
      )
        .then((_) => {
          dispatch(fetchActiveUserAction()).then((_) => {
            history.push(
              !location?.state?.referrer?.includes('auth')
                ? location?.state?.referrer
                :window.location.hostname.includes("studentop") ?  '/services' :""
            );
          });
        })
        .catch((err) => {
          if (err.response.status >= 400) {
            message.error({
              content: t('bad_credentials'),
              style: {
                marginTop: '10vh',
                fontSize: 16,
              },
            });
            setAuthStep('login');
            setDetailsForTwoFactor();
            form.resetFields();
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    });
  }

  function handleCaptcha(token) {
    return setIsCaptchaValid(!!token);
  }

  function renderAuthStep() {
    if (authStep === 'login') {
      return (
        <Form
          form={form}
          name="normal_login"
          className="login-form"
          initialValues={{ remember: true }}
          onFinish={onLoginFinish}
        >
          <Form.Item
            name="idNumber"
            rules={[
              {
                required: true,
                message: t('required', {
                  field: t(
                    getWeboConfig('STORE_USE_PHONE_TO_LOGIN')
                      ? 'phone_number'
                      : 'id_number'
                  ),
                }),
              },
            ]}
          >
            <Input
              prefix={<UserOutlined className="site-form-item-icon" />}
              placeholder={t(
                getWeboConfig('STORE_USE_PHONE_TO_LOGIN')
                  ? 'phone_number'
                  : 'id_number'
              )}
            />
          </Form.Item>
          <Form.Item
            name="email"
            rules={[
              {
                required: true,
                message: t('required', { field: t('email') }),
              },
            ]}
          >
            <Input
              prefix={<MailOutlined className="site-form-item-icon" />}
              type="email"
              placeholder={t('email')}
            />
          </Form.Item>
          <FlexDivCenter>
            <Form.Item>
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox style={{ direction: 'rtl' }}>
                  {t('remember_me_on_this_device')}
                </Checkbox>
              </Form.Item>
            </Form.Item>
          </FlexDivCenter>
          <FlexDivCenter>
            <Form.Item name="captcha">
              <ReCAPTCHA
                size="compact"
                sitekey="6LdfBLYZAAAAAMRkYqlePow956u8Up4vXIBywkNb"
                onChange={handleCaptcha}
              />
            </Form.Item>
          </FlexDivCenter>
          <FlexDivCenter>
            <Form.Item>
              <Button
                loading={isLoading}
                disabled={!isCaptchaValid}
                shape="round"
                type="primary"
                htmlType="submit"
                className="login-form-button"
              >
                {t('login')}
              </Button>
            </Form.Item>
          </FlexDivCenter>
          <FlexDivCenter
            style={{ background: 'white', borderRadius: 5, padding: 5 }}
            margin=" 0"
          >
            <Text size="large">{t('sua')}</Text>
          </FlexDivCenter>
          <FlexDivCenter>
            <a
              className="login-form-forgot"
              href={`mailto: ${getWeboConfig('STORE_SUPPORT_EMAIL')}`}
            >
              {t('having_trouble')}
            </a>
          </FlexDivCenter>
        </Form>
      );
    }
    if (authStep === '2fa') {
      return (
        <Form
          form={form}
          name="normal_login"
          className="login-form"
          initialValues={{ remember: true }}
          onFinish={onTwoFactorFinish}
        >
          <FlexDivCenter>
            <Text size="large">{t('what_is_the_verification_code')}</Text>
          </FlexDivCenter>
          <FlexDivCenter>
            <Text>{t('check_in_spam')}</Text>
          </FlexDivCenter>
          <Form.Item
            name="verificationCode"
            rules={[
              {
                required: true,
                message: t('required', {
                  field: getWeboConfig('STORE_USE_PHONE_TO_LOGIN')
                    ? 'phone_number'
                    : 'id_number',
                }),
              },
            ]}
          >
            <Input
              prefix={<LockOutlined className="site-form-item-icon" />}
              placeholder={t('verification_code')}
            />
          </Form.Item>
          <FlexDivCenter>
            <Form.Item>
              <Button
                loading={isLoading}
                disabled={!isCaptchaValid}
                shape="round"
                type="primary"
                htmlType="submit"
                className="login-form-button"
              >
                {t('verify')}
              </Button>
            </Form.Item>
          </FlexDivCenter>
        </Form>
      );
    }
  }

  return (
    <AuthWrapper>
      <FlexDivCenter>
        <FormWrapper isMobile={isMobile}>
          <Card style={{ borderRadius: 5 }} title={`${t('login')}`}>
            {renderAuthStep()}
          </Card>
        </FormWrapper>
      </FlexDivCenter>
    </AuthWrapper>
  );
};
